// The record trail (F-178, F-185 docs/PAGE-GRAMMAR.md): where a record screen was opened from, so its header can say
// "Back to <section>" and step to the Previous / Next record of that list. Once installed (at the first import), a
// click on an in-app link inside the detail pane (#main) that leaves a screen which is not itself a record page records
// that screen: its hash (filters kept), the navigation item it sits under (else its heading) and the in-app links it
// showed, in order. A record screen reads the trail through `recordNav`: when the trail's links hold this record, Back
// returns to that list and the neighbours are its neighbours there; otherwise (a deep link, a reload in another tab)
// Back is the section's own route and the neighbours come from the host's id-ordered list, when it has one.
// Kept per tab in sessionStorage, read and written inside try/catch; nothing here is rendered.

import { useEffect, useState } from "react";

export interface Trail {
  /** The list's hash, query kept (`#requisitions?state=open`). */
  from: string;
  /** The section's name as the screen showed it. */
  label: string;
  /** The in-app links the screen showed, in order, each once. */
  items: string[];
}

export interface RecordNavOf {
  section: { label: string; href: string };
  neighbours: { previous: string | null; next: string | null };
}

const KEY = "ta.recordTrail";
const listeners = new Set<() => void>();
let trail: Trail | null = read();

function read(): Trail | null {
  try {
    const raw = typeof window === "undefined" ? null : window.sessionStorage.getItem(KEY);
    const value = raw ? (JSON.parse(raw) as Trail) : null;
    return value && typeof value.from === "string" && typeof value.label === "string" && Array.isArray(value.items) ? value : null;
  } catch {
    return null;
  }
}

function keep(next: Trail): void {
  trail = next;
  try {
    window.sessionStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    // a private window keeps the trail for this page only
  }
  for (const notify of listeners) notify();
}

const pathOf = (hash: string) => (hash.replace(/^#/, "").split("?")[0] ?? "").replace(/\/+$/, "");
const words = (element: Element | null | undefined) => (element?.textContent ?? "").replace(/\s+/g, " ").trim();

/** The section a list sits under: the lit navigation item's name, else the screen's heading. */
function sectionLabel(main: Element): string {
  return words(document.querySelector("[aria-current='page'] .mc-nav-t")) || words(main.querySelector("h1")) || "";
}

/** Records the list a click leaves from (capture phase, so the screen's own handlers still run). */
export function onTrailClick(event: Pick<MouseEvent, "target">): void {
  const link = (event.target as Element | null)?.closest?.("a[href^='#']");
  const main = document.getElementById("main");
  if (!link || !main || !main.contains(link)) return;
  // a click on a record page (Previous, Next, a tab, a deeper link) keeps the list it was opened from
  if (main.querySelector(".mc-rp")) return;
  const from = window.location.hash || "#";
  const href = link.getAttribute("href") ?? "";
  if (pathOf(href) === pathOf(from)) return;
  const label = sectionLabel(main);
  if (!label) return;
  const seen = new Set<string>();
  const items: string[] = [];
  for (const a of main.querySelectorAll("a[href^='#']")) {
    const h = a.getAttribute("href") ?? "";
    if (h.length > 1 && !seen.has(h)) { seen.add(h); items.push(h); }
  }
  keep({ from, label, items });
}

let installed = false;
function install(): void {
  if (installed || typeof document === "undefined") return;
  installed = true;
  document.addEventListener("click", onTrailClick, true);
}
install();

/** The trail as it stands; a record screen re-renders when it changes. */
export function useTrail(): Trail | null {
  const [, tick] = useState(0);
  useEffect(() => {
    const notify = () => tick((n) => n + 1);
    listeners.add(notify);
    return () => { listeners.delete(notify); };
  }, []);
  return trail;
}

/** Previous and next of `self` in a list of record links; null at the ends; none when `self` is not in it. */
export function neighboursIn(list: readonly string[], self: string): RecordNavOf["neighbours"] | null {
  const at = list.indexOf(self);
  if (at < 0) return null;
  return { previous: list[at - 1] ?? null, next: list[at + 1] ?? null };
}

/**
 * Where a record screen goes back to and steps to. `self` is the record's own link (`#requisitions/R-1048`), `isRecord`
 * tells this kind of record's links from the rest of a list, `section` is the fallback (the section's own route and
 * name) and `ordered` the host's id-ordered list of this kind of record, used when the trail does not hold the record.
 */
export function recordNav(remembered: Trail | null, self: string, isRecord: (href: string) => boolean, section: RecordNavOf["section"], ordered: readonly string[] = []): RecordNavOf {
  const listed = remembered ? remembered.items.filter(isRecord) : [];
  const there = remembered ? neighboursIn(listed, self) : null;
  if (remembered && there) return { section: { label: remembered.label, href: remembered.from }, neighbours: there };
  return { section, neighbours: neighboursIn(ordered, self) ?? { previous: null, next: null } };
}
